/*
 * Model for Engines
 */
define([
  "backbone",
  "jquery",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
], function (Backbone, $, Messenger, Properties, SortableListModel) {
  const EngineAdminModel = {};

  EngineAdminModel.engine = Backbone.Model.extend({
    idAttribute: "engineId",
    defaultValidateAttributes: [
      "engineName",
      "engineDescription",
      "soapDomain",
      "kbCapacity",
      "internalUrl",
    ],
    defaults: {
      engineName: "",
      engineDescription: "",
      soapDomain: "",
      kbCapacity: {},
      internalUrl: "",
    },
    backup: function () {
      this.onSynced(function () {
        this.wasDirty = this.dirty;
        this.cloneCopy = this.clone();
      }, this);
    },
    restore: function () {
      this.set(this.cloneCopy.attributes);
      this.dirty = this.wasDirty;
    },
    _validateDuplicateEngineName: function (value) {
      if (this.collection) {
        let duplicateName = false;
        this.collection.each(function (engine) {
          //check if the other object has the same name (ignore case) and is actually a different object
          if (
            engine.get("engineName").toLowerCase() === value.toLowerCase() &&
            engine.cid !== this.cid
          ) {
            duplicateName = true;
          }
        }, this);
        if (duplicateName) return $.t("admin.engineAdmin.duplicateNameError");
      }
    },
    _validateDuplicateSoapDomain: function (value) {
      if (this.collection) {
        let duplicateSoapDomain = false;
        this.collection.each(function (engine) {
          //check if the other object has the same name (ignore case) and is actually a different object
          if (
            engine.get("soapDomain").toLowerCase() === value.toLowerCase() &&
            engine.cid !== this.cid
          ) {
            duplicateSoapDomain = true;
          }
        }, this);
        if (duplicateSoapDomain) return $.t("admin.engineAdmin.duplicateSoapError");
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "engineName") {
        return (
          this.validateRequiredValue(value, { maxLength: 50 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateEngineName(value)
        );
      }
      if (attribute === "engineDescription") {
        //Engine description must be less than 255 chars
        return this.validateStringLength(value, { maxLength: 255 });
      }
      if (attribute === "soapDomain") {
        return (
          this.validateRequiredValue(value, { maxLength: 255 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateSoapDomain(value)
        );
      }
      if (attribute === "kbCapacity") {
        if (
          value.capacity === null ||
          value.capacity === undefined ||
          value.capacity.length === 0
        ) {
          return $.t("commonMessages.emptyMessage");
        }
        if (isNaN(value.capacity) || !isFinite(value.capacity))
          return $.t("commonMessages.notNumeric");
        if (parseInt(value.capacity) !== value.capacity) return $.t("commonMessages.notInteger");
        if (value.capacity <= 0) return $.t("commonMessages.notPositive");
        if (parseInt(value.capacity) > 2147483647)
          return $.t("commonMessages.tooBig", { number: 2147483647 });
        if (parseInt(value.capacity) < value.usage) {
          return $.t("admin.engineAdmin.usageExceedsCapacity", { count: value.usage });
        }
      }
      if (attribute == "internalUrl") {
        return this.validateRequiredValue(value, { maxLength: 500 });
      }
    },
  });

  EngineAdminModel.engines = Backbone.Collection.extend({
    model: EngineAdminModel.engine,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "engineName");
    },
    addItem: function () {
      const newItem = new EngineAdminModel.engine();
      this.add(newItem);
      return newItem;
    },
    url: function () {
      return Properties.adminRootURL + "/engines";
    },
  });

  EngineAdminModel.engineKnowledgeBase = Backbone.Model.extend({
    defaults: {
      engineId: 0,
      engineName: "",
      tenantId: 0,
      tenantName: "",
      kbId: 0,
      kbName: "",
      kbDescription: "",
      lastLoad: null,
      lastUpdate: null,
      lastUnload: null,
      loadOnStart: true,
      loaded: false,
      avgRefreshTime: 0,
    },
  });

  EngineAdminModel.engineKnowledgeBases = Backbone.Collection.extend({
    model: EngineAdminModel.engineKnowledgeBase,
    comparator: function (a, b) {
      let result = Backbone.textComparator(a, b, "engineName");
      if (result === 0) {
        result = Backbone.textComparator(a, b, "kbName");
      }
      return result;
    },
    addItem: function (engine, knowledgeBase) {
      if (!engine) {
        throw new Error("Engine is required to add a Knowledge Base to an Engine");
      }
      if (!knowledgeBase) {
        throw new Error("Knowledge Base is required to add a Knowledge Base to an Engine");
      }
      const engineKnowledgeBase = new EngineAdminModel.engineKnowledgeBase({
        engineId: engine.get("engineId"),
        engineName: engine.get("engineName"),
        tenantId: knowledgeBase.get("tenantId"),
        tenantName: knowledgeBase.get("tenantName"),
        kbId: knowledgeBase.get("kbId"),
        kbName: knowledgeBase.get("kbName"),
        loaded: false,
        loadOnStart: true,
      });
      this.add(engineKnowledgeBase);
      return engineKnowledgeBase;
    },
    url: function () {
      return Properties.adminRootURL + "/engines/kbs";
    },
  });

  Messenger.reqres.setHandler(
    "engines",
    function () {
      const engines = new EngineAdminModel.engines();
      engines.fetch();
      return engines;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "engineKbs",
    function () {
      const engineKnowledgeBases = new EngineAdminModel.engineKnowledgeBases();
      engineKnowledgeBases.fetch();
      return engineKnowledgeBases;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "engine:admin:headers",
    function () {
      const Header = SortableListModel.headerModel;
      return new Backbone.Collection([
        new Header({
          headerName: $.t("admin.engineAdmin.engineName"),
          modelAttribute: "engineName",
          selectable: true,
          sortable: true,
          filterable: false,
        }),
        new Header({
          headerName: $.t("admin.engineAdmin.engineDescription"),
          modelAttribute: "engineDescription",
          longTextField: true,
          sortable: false,
          hidden: true,
          hideable: true,
          filterable: false,
        }),
        new Header({
          headerName: $.t("admin.engineAdmin.soapDomain"),
          modelAttribute: "soapDomain",
          longTextField: true,
          sortable: false,
          filterable: false,
        }),
        new Header({
          headerName: $.t("admin.engineAdmin.kbCapacity"),
          modelAttribute: "kbCapacity",
          sortable: false,
          filterable: false,
          customHtml: "<%- kbCapacity.usage %> / <%- kbCapacity.capacity %>",
        }),
        new Header({
          headerName: "",
          modelAttribute: "engineName",
          width: "15px",
          customHtml:
            '<button type="button" class="btn btn-danger btn-mini deleteEngine' +
            '<% if (kbCapacity.usage) { %> disabled" data-i18n="[title]admin.engineAdmin.deleteEngineInUse" ' +
            '<% } else { %>" data-i18n="[title]admin.engineAdmin.deleteEngine" <% } %> data-toggle="tooltip">' +
            '<i class="icon-remove"></i></button>',
        }),
      ]);
    },
    this,
  );

  Messenger.reqres.setHandler(
    "engineKb:admin:headers",
    function () {
      const Header = SortableListModel.headerModel;
      const yesValue = $.t("common.yes");
      const noValue = $.t("common.no");
      return new Backbone.Collection([
        new Header({
          headerName: $.t("admin.engineAdmin.engineName"),
          modelAttribute: "engineName",
          textField: true,
          sortable: true,
          filterable: true,
        }),
        new Header({
          headerName: $.t("admin.engineAdmin.tenantName"),
          modelAttribute: "tenantName",
          longTextField: true,
          sortable: true,
          filterable: true,
        }),
        new Header({
          headerName: $.t("admin.engineAdmin.kbName"),
          modelAttribute: "kbName",
          longTextField: true,
          sortable: true,
          filterable: true,
        }),
        new Header({
          headerName: $.t("admin.engineAdmin.kbDescription"),
          modelAttribute: "kbDescription",
          longTextField: true,
          sortable: true,
          filterable: true,
          hidden: true,
          hideable: true,
        }),
        new Header({
          headerName: $.t("admin.engineAdmin.loaded"),
          modelAttribute: "loaded",
          hideable: true,
          customHtml: (engineKb) => (engineKb.get("loaded") ? yesValue : noValue),
        }),
        new Header({
          headerName: $.t("admin.engineAdmin.loadTime"),
          modelAttribute: "lastLoad",
          dateField: true,
          sortable: true,
          filterable: true,
          hidden: true,
          hideable: true,
        }),
        new Header({
          headerName: $.t("admin.engineAdmin.updateTime"),
          modelAttribute: "lastUpdate",
          dateField: true,
          sortable: true,
          filterable: true,
          hidden: true,
          hideable: true,
        }),
        new Header({
          headerName: $.t("admin.engineAdmin.unloadTime"),
          modelAttribute: "lastUnload",
          dateField: true,
          sortable: true,
          filterable: true,
          hidden: true,
          hideable: true,
        }),
        new Header({
          headerName: $.t("admin.engineAdmin.avgRefreshTime"),
          modelAttribute: "avgRefreshTime",
          durationMillisecondsField: true,
          sortable: true,
          hidden: true,
          hideable: true,
        }),
        new Header({
          headerName: "",
          modelAttribute: "kbName",
          width: "15px",
          customHtml:
            '<button type="button" class="btn btn-danger btn-mini deleteEngineKb" ' +
            'data-i18n="[title]admin.engineAdmin.removeKb" data-toggle="tooltip">' +
            '<i class="icon-remove"></button>',
        }),
      ]);
    },
    this,
  );

  return EngineAdminModel;
});
